<template>
  <SoSimpleTable
    :headers="tableHeaders"
    :items="tableItems"
    is-pivoted
    show-copy-to-clipboard
  />
  <p class="mt-4 ml-4">
    <i>Prices shown include VAT.</i>
  </p>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import dayjs from "dayjs"
  import advancedFormat from "dayjs/plugin/advancedFormat"
  import {
    IMeterPointAgreement,
    ITariffRate,
  } from "@/models/meterpointAgreement/meterpointAgreement.types"
  import { RateNames } from "@/constants/agreements"
  import { formatPriceInPence } from "@/helpers/moneyHelpers"
  import { formatVatLabel } from "@/helpers/vatHelper"

  dayjs.extend(advancedFormat)

  export default defineComponent({
    components: { SoSimpleTable },
    props: {
      agreements: {
        type: Array as PropType<IMeterPointAgreement[]>,
        default: () => {
          return []
        },
      },
    },
    computed: {
      hasDualRatePeakOffPeakAgreements(): boolean {
        return this.agreements.some((agreement) => agreement.isElecEvTariff)
      },
      hasDualRateDayNightAgreements(): boolean {
        return this.agreements.some((agreement) => agreement.isEco7Product)
      },
      hasSingleRateAgreements(): boolean {
        return this.agreements.some((agreement) => agreement.isStandardProduct)
      },
      hasBespokeRateAgreements(): boolean {
        return this.agreements.some((agreement) => agreement.isSoUniqueTariff)
      },
      tableHeaders(): string[] {
        const headers = ["Name", "From Date", "To Date"]
        if (this.hasSingleRateAgreements) {
          headers.push("Unit Rate")
        }
        if (this.hasBespokeRateAgreements) {
          const soUniqueAgreement = this.agreements.find(
            (agreement) => agreement.isSoUniqueTariff,
          )
          const peakTimes = this.formatSoUniqueTariffRateTimes(
            soUniqueAgreement,
            RateNames.SoUniquePeak,
          )
          const offPeakTimes = this.formatSoUniqueTariffRateTimes(
            soUniqueAgreement,
            RateNames.SoUniqueOffPeak,
          )
          const superOffPeakTimes = this.formatSoUniqueTariffRateTimes(
            soUniqueAgreement,
            RateNames.SoUniqueSuperOffPeak,
          )

          headers.push(
            `Unit Rate ${RateNames.SoUniquePeak} (${peakTimes})`,
            `Unit Rate ${RateNames.SoUniqueOffPeak} (${offPeakTimes})`,
            `Unit Rate ${RateNames.SoUniqueSuperOffPeak} (${superOffPeakTimes})`,
          )
        }
        if (this.hasDualRatePeakOffPeakAgreements) {
          const evAgreement = this.agreements.find(
            (agreement) => agreement.isElecEvTariff,
          )

          const evPeakTimes = this.getEvTariffRateTimes(
            evAgreement,
            RateNames.EvPeak,
          )
          const evOffPeakTimes = this.getEvTariffRateTimes(
            evAgreement,
            RateNames.EvOffPeak,
          )

          const peakHeader = `Unit Rate Peak (${evPeakTimes.fromTime} - ${evPeakTimes.toTime})`
          const offPeakHeader = `Unit Rate Off-Peak (${evOffPeakTimes.fromTime} - ${evOffPeakTimes.toTime})`

          headers.push(peakHeader)
          headers.push(offPeakHeader)
        }
        if (this.hasDualRateDayNightAgreements) {
          headers.push("Unit Rate (Day)")
          headers.push("Unit Rate (Night)")
        }
        headers.push(
          "Duration of Tariff",
          "Standing Charge",
          "Early Exit Fee",
          "VAT",
        )

        return headers
      },
      tableItems(): Array<string[]> {
        return this.agreements.map((agreement) => {
          const values: string[] = []

          const name = agreement.productDetails.displayName
          const fromDate = agreement.fromDate
            ? dayjs(agreement.fromDate).format("Do MMM YY")
            : "no start date"
          const toDate = agreement.toDate
            ? dayjs(agreement.toDate).format("Do MMM YY")
            : "no end date"

          values.push(name)
          values.push(fromDate)
          values.push(toDate)

          if (this.hasSingleRateAgreements) {
            const standardRate = agreement.productDetails.tariffRates?.find(
              ({ name }) => name === RateNames.Standard,
            )
            values.push(this.formatRate(standardRate))
          }

          if (this.hasBespokeRateAgreements) {
            const peakRate = agreement.productDetails.tariffRates?.find(
              (rate) => rate.name === RateNames.SoUniquePeak,
            )
            const offPeakRate = agreement.productDetails.tariffRates?.find(
              (rate) => rate.name === RateNames.SoUniqueOffPeak,
            )
            const superOffPeakRate = agreement.productDetails.tariffRates?.find(
              (rate) => rate.name === RateNames.SoUniqueSuperOffPeak,
            )
            values.push(
              this.formatRate(peakRate),
              this.formatRate(offPeakRate),
              this.formatRate(superOffPeakRate),
            )
          }

          if (this.hasDualRatePeakOffPeakAgreements) {
            const evPeakRate = agreement.productDetails.tariffRates?.find(
              (rate) => rate.name === RateNames.EvPeak,
            )
            const evOffPeakRate = agreement.productDetails.tariffRates?.find(
              (rate) => rate.name === RateNames.EvOffPeak,
            )
            values.push(
              this.formatRate(evPeakRate),
              this.formatRate(evOffPeakRate),
            )
          }

          if (this.hasDualRateDayNightAgreements) {
            const dayRate = agreement.productDetails.tariffRates?.find(
              (rate) => rate.name === RateNames.Day,
            )
            const nightRate = agreement.productDetails.tariffRates?.find(
              (rate) => rate.name === RateNames.Night,
            )
            values.push(this.formatRate(dayRate))
            values.push(this.formatRate(nightRate))
          }

          const duration = agreement.productDetails.durationInMonths
            ? `${agreement.productDetails.durationInMonths} months`
            : "-"
          const standingCharge = agreement.productDetails.standingChargeInPence
            ? `${formatPriceInPence(
                agreement.productDetails.standingChargeInPence,
              )}/day`
            : "-"
          const earlyExitFee = agreement.productDetails
            .earlyTerminationChargeInPounds
            ? `£${agreement.productDetails.earlyTerminationChargeInPounds}`
            : "-"

          values.push(
            duration,
            standingCharge,
            earlyExitFee,
            formatVatLabel(agreement.productDetails.vatRate) ?? "-",
          )

          return values
        })
      },
    },
    methods: {
      getEvTariffRateTimes(
        evAgreement: IMeterPointAgreement | undefined,
        rateName: string,
      ) {
        const rate = evAgreement?.productDetails.tariffRates?.find(
          (rate) => rate.name === rateName,
        )
        return {
          fromTime: rate?.startHour,
          toTime: rate?.endHour,
        }
      },
      getSoUniqueTariffRateTimes(
        soUniqueAgreement: IMeterPointAgreement | undefined,
        rateName: string,
      ) {
        const rates = soUniqueAgreement?.productDetails.tariffRates?.filter(
          (rate) => rate.name === rateName,
        )
        return (
          rates?.map((rate) => ({
            fromTime: rate.startHour,
            toTime: rate.endHour,
          })) || []
        )
      },
      formatSoUniqueTariffRateTimes(
        soUniqueAgreement: IMeterPointAgreement | undefined,
        rateName: (typeof RateNames)[keyof typeof RateNames],
      ) {
        return this.getSoUniqueTariffRateTimes(soUniqueAgreement, rateName)
          .map(this.formatTimeRange)
          .join(", ")
      },
      formatRate(rate: ITariffRate | undefined): string {
        if (!rate?.rateInPence) return "-"
        return `${formatPriceInPence(rate.rateInPence)}/kWh`
      },
      formatTimeRange(time: {
        fromTime: string | undefined
        toTime: string | undefined
      }): string {
        return time.fromTime && time.toTime
          ? `${time.fromTime} - ${time.toTime}`
          : ""
      },
    },
  })
</script>
